'use client';

import React from 'react';
import { Button, Faq, MarketingSurface, useTalkToUs } from '@/components/marketing';
import { landingPageEvents } from '@/components/PostHogProvider';

interface FaqPair {
  readonly question: string;
  readonly answer: string;
}

// No 'editorial': that variant is a centered 900px column, and research pages sit
// on the full rail (`ResearchRail`), where it would float off their left edge.
type ResearchFaqVariant = 'split' | 'homepage';

/**
 * The FAQ band shared by the research deep-dives: the shared `Faq` section in
 * the homepage design, wrapped in the page's one `MarketingSurface` so its
 * tokens resolve on a page that is otherwise Tailwind-styled. The surface
 * carries `.faq-section`, the speakable selector both pages declare. This file
 * owns only what is the research pages' – the heading, the `talk-to-us`
 * action at the `research-faq` location and the `landing_faq_expanded` event.
 * Each page keeps its `FAQS` array and feeds the same pairs to
 * `FAQPageSchema`, so the schema is untouched by the design.
 *
 * Only the `split` variant wraps its own `MarketingSurface`: `homepage` renders
 * on pages that already sit inside `InteriorPage`, and a surface is never nested.
 *
 * Row ids are positional (`research-faq-1`…): the pairs carry no id, and
 * nothing in the JSON-LD path reads one.
 */
export function ResearchFaq({
  faqs,
  variant = 'split',
  heading = 'Questions about this research',
  cardBody,
}: {
  faqs: readonly FaqPair[];
  variant?: ResearchFaqVariant;
  /** The section heading; the default names the page as research. */
  heading?: string;
  /**
   * Body copy for the conversation card. The `split` band runs the single-line
   * invitation; the homepage layout has room for a sentence.
   */
  cardBody?: string;
}): React.ReactElement {
  const onTalkToUs = useTalkToUs('research-faq');

  const content = (
    <Faq
      id="faq"
      variant={variant}
      eyebrow="Common questions"
      heading={heading}
      card={{
        heading: 'Want to discuss the findings?',
        body: cardBody,
        cta: (
          <Button aria-haspopup="dialog" onClick={onTalkToUs}>
            Talk to us
          </Button>
        ),
      }}
      items={faqs.map((faq, index) => ({
        id: `research-faq-${index + 1}`,
        question: faq.question,
        answer: faq.answer,
      }))}
      onExpand={(id) => landingPageEvents.faqExpanded(id)}
    />
  );
  return variant === 'split' ? (
    <MarketingSurface className="faq-section">{content}</MarketingSurface>
  ) : (
    <div className="faq-section">{content}</div>
  );
}
